import { EnterForm } from '../components/EnterForm';
import React, { useState, useEffect } from 'react';
import { usePageCallbacks } from '../hooks/usePageCallbacks';
import {
  getProducts,
  getInventoryOverview, getProductStockDetail, getNextBatchNumber, createBatch, deleteBatch,
  updateProductThreshold,
} from '../lib/api';
import { formatApiErrorDetail, formatDate, getToday } from '../lib/utils';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { Alert, AlertDescription } from '../components/ui/alert';
import { Badge } from '../components/ui/badge';
import { Skeleton } from '../components/ui/skeleton';
import { SearchableSelect } from '../components/ui/searchable-select';
import { DateInput } from '../components/ui/date-input';
import { Plus, AlertCircle, Trash2, ChevronDown, ChevronUp, AlertTriangle, Settings2 } from 'lucide-react';
import { ExportToolbar } from '../components/ui/export-toolbar';
import { useSortableTable, SortableHeader } from '../components/ui/sortable-table';
import { FormShortcutHints } from '../components/ui/form-shortcut-hints';
import { toast } from 'sonner';

const CATEGORY_LABELS = {
  raw_material: 'Raw Material',
  in_process: 'In-Process',
  finished_goods: 'Finished Goods',
};

const toTons = (kg) => kg ? `${(kg / 1000).toFixed(2)}` : '0.00';

const Inventory = () => {
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [overview, setOverview] = useState(null);
  const [overviewLoading, setOverviewLoading] = useState(false);
  const [filterCat, setFilterCat] = useState('');
  const [expandedProduct, setExpandedProduct] = useState(null);
  const [productBatches, setProductBatches] = useState({});

  // Sorting
  const { sortedData: sortedProducts, sortKey, sortDirection, handleSort } = useSortableTable(overview?.products || [], 'product_name', 'asc');

  // Batch dialog
  const [batchDialogOpen, setBatchDialogOpen] = useState(false);
  const [batchForm, setBatchForm] = useState({ product_id: '', batch_number: '', brand: '', date: getToday(), quantity_kg: '', quantity_bags: '', remarks: '' });
  const [batchError, setBatchError] = useState('');
  const [batchSubmitting, setBatchSubmitting] = useState(false);
  const [batchProductName, setBatchProductName] = useState('');

  // Threshold dialog
  const [thresholdDialog, setThresholdDialog] = useState(false);
  const [thresholdProduct, setThresholdProduct] = useState(null);
  const [thresholdValue, setThresholdValue] = useState('');
  const [thresholdSubmitting, setThresholdSubmitting] = useState(false);

  usePageCallbacks({
    onNewEntry: () => { openBatchDialog(); },
  });

  useEffect(() => { fetchData(); fetchOverview(); }, []); // eslint-disable-line

  const fetchData = async () => {
    try {
      const prodRes = await getProducts();
      setProducts(prodRes.data);
    } catch (err) { console.error('Failed to load products:', err); toast.error('Failed to load data'); }
    finally { setLoading(false); }
  };

  const fetchOverview = async (cat) => {
    setOverviewLoading(true);
    try {
      const res = await getInventoryOverview(cat || filterCat);
      setOverview(res.data);
    } catch (err) { console.error('Failed to load inventory overview:', err); toast.error('Failed to load inventory'); }
    finally { setOverviewLoading(false); }
  };

  const handleCatFilter = (cat) => {
    setFilterCat(cat);
    fetchOverview(cat);
  };

  const toggleExpand = async (productId) => {
    if (expandedProduct === productId) { setExpandedProduct(null); return; }
    setExpandedProduct(productId);
    if (!productBatches[productId]) {
      try {
        const res = await getProductStockDetail(productId);
        setProductBatches(prev => ({ ...prev, [productId]: res.data }));
      } catch (err) { console.error('Failed to load batches:', err); toast.error('Failed to load batches'); }
    }
  };

  const handleDeleteBatch = async (batch) => {
    if (!window.confirm(`Delete batch "${batch.batch_number}"?`)) return;
    try {
      await deleteBatch(batch.id);
      toast.success('Batch deleted');
      const res = await getProductStockDetail(batch.product_id);
      setProductBatches(prev => ({ ...prev, [batch.product_id]: res.data }));
      fetchOverview();
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
  };

  // Batch dialog
  const openBatchDialog = (productId, productName) => {
    setBatchForm({ product_id: productId || '', batch_number: '', brand: '', date: getToday(), quantity_kg: '', quantity_bags: '', remarks: '' });
    setBatchProductName(productName || '');
    setBatchError('');
    setBatchDialogOpen(true);
    if (productId) fetchNextBatchNumber(productId);
  };

  const fetchNextBatchNumber = async (productId) => {
    try {
      const res = await getNextBatchNumber(productId);
      setBatchForm(prev => ({ ...prev, batch_number: res.data.batch_number }));
    } catch (err) { console.error('Next batch number fetch failed:', err); }
  };

  const handleBatchProductChange = (v) => {
    setBatchForm(prev => ({ ...prev, product_id: v }));
    const p = products.find(p => p.id === v);
    setBatchProductName(p?.name || '');
    if (v && p?.category === 'finished_goods') fetchNextBatchNumber(v);
  };

  const handleBatchKgChange = (val) => {
    setBatchForm(prev => {
      const p = products.find(p => p.id === prev.product_id);
      const bags = p && p.kg_per_bag ? (parseFloat(val) / p.kg_per_bag).toFixed(1) : prev.quantity_bags;
      return { ...prev, quantity_kg: val, quantity_bags: bags };
    });
  };

  const handleCreateBatch = async (e) => {
    e.preventDefault(); setBatchError(''); setBatchSubmitting(true);
    try {
      const p = products.find(p => p.id === batchForm.product_id);
      await createBatch({
        product_id: batchForm.product_id,
        batch_number: batchForm.batch_number || '',
        brand: batchForm.brand,
        date: batchForm.date,
        quantity_kg: parseFloat(batchForm.quantity_kg),
        quantity_bags: batchForm.quantity_bags ? parseFloat(batchForm.quantity_bags) : null,
        category: p?.category || '',
        remarks: batchForm.remarks,
      });
      toast.success('Batch created');
      setBatchDialogOpen(false);
      fetchOverview();
      if (expandedProduct === batchForm.product_id) {
        const res = await getProductStockDetail(batchForm.product_id);
        setProductBatches(prev => ({ ...prev, [batchForm.product_id]: res.data }));
      }
    } catch (err) { setBatchError(formatApiErrorDetail(err.response?.data?.detail)); }
    finally { setBatchSubmitting(false); }
  };

  // Threshold
  const openThresholdDialog = (product) => {
    setThresholdProduct(product);
    setThresholdValue(product.low_stock_threshold ? String(product.low_stock_threshold) : '');
    setThresholdDialog(true);
  };

  const handleSaveThreshold = async () => {
    setThresholdSubmitting(true);
    try {
      await updateProductThreshold(thresholdProduct.product_id, parseFloat(thresholdValue) || 0);
      toast.success('Threshold updated');
      setThresholdDialog(false);
      fetchOverview();
    } catch (err) { toast.error('Failed to update threshold'); }
    finally { setThresholdSubmitting(false); }
  };

  const productOptions = products.map(p => ({ value: p.id, label: `${p.name} (${p.group})` }));

  if (loading) return <div className="space-y-6"><Skeleton className="h-8 w-32" /><Skeleton className="h-64 w-full" /></div>;

  const summary = overview?.summary || {};
  const lowStockCount = summary.low_stock_count || 0;

  return (
    <div className="space-y-4" data-testid="inventory-page">
      <div className="flex items-center justify-between">
        <h1 className="text-lg sm:text-xl font-bold">Inventory</h1>
        <div className="flex gap-2">
          <ExportToolbar endpoint="/exports/inventory-stock" fileBaseName="inventory-stock" params={{ category: filterCat || undefined }} testId="export-inventory" />
          <Button size="sm" onClick={() => openBatchDialog()} data-testid="add-batch-btn"><Plus className="h-4 w-4 mr-1" />Add Batch</Button>
        </div>
      </div>

      {overviewLoading ? <Skeleton className="h-64 w-full" /> : overview && (
        <>
          {/* Summary Cards */}
          <div className="grid grid-cols-2 sm:grid-cols-5 gap-3">
            <Card className="border-l-4 border-l-blue-500">
              <CardContent className="px-3 py-2.5">
                <p className="text-xs text-muted-foreground">On-Hand</p>
                <p className="text-lg font-bold font-mono">{toTons(summary.total_on_hand)} <span className="text-xs font-normal">Tons</span></p>
              </CardContent>
            </Card>
            <Card className="border-l-4 border-l-amber-500">
              <CardContent className="px-3 py-2.5">
                <p className="text-xs text-muted-foreground">PO Pending <span className="text-[10px]">(Inbound)</span></p>
                <p className="text-lg font-bold font-mono text-amber-600">{toTons(summary.total_po_pending)} <span className="text-xs font-normal">Tons</span></p>
              </CardContent>
            </Card>
            <Card className="border-l-4 border-l-orange-500">
              <CardContent className="px-3 py-2.5">
                <p className="text-xs text-muted-foreground">SO Pending <span className="text-[10px]">(Outbound)</span></p>
                <p className="text-lg font-bold font-mono text-orange-600">{toTons(summary.total_so_pending)} <span className="text-xs font-normal">Tons</span></p>
              </CardContent>
            </Card>
            <Card className="border-l-4 border-l-green-500">
              <CardContent className="px-3 py-2.5">
                <p className="text-xs text-muted-foreground">Available</p>
                <p className="text-lg font-bold font-mono text-green-600">{toTons(summary.total_available)} <span className="text-xs font-normal">Tons</span></p>
              </CardContent>
            </Card>
            <Card className="border-l-4 border-l-violet-500">
              <CardContent className="px-3 py-2.5">
                <p className="text-xs text-muted-foreground">Projected</p>
                <p className="text-lg font-bold font-mono text-violet-600">{toTons(summary.total_projected)} <span className="text-xs font-normal">Tons</span></p>
              </CardContent>
            </Card>
          </div>

          {/* Low stock alert */}
          {lowStockCount > 0 && (
            <Alert variant="destructive" className="border-amber-500 bg-amber-50 dark:bg-amber-950/20 text-amber-900 dark:text-amber-200">
              <AlertTriangle className="h-4 w-4" />
              <AlertDescription>
                <span className="font-semibold">{lowStockCount} product{lowStockCount > 1 ? 's' : ''}</span> below low stock threshold
              </AlertDescription>
            </Alert>
          )}

          {/* Category filter */}
          <div className="flex flex-wrap items-center gap-2">
            <Button variant={!filterCat ? 'default' : 'outline'} size="sm" className="text-xs h-7" onClick={() => handleCatFilter('')}>All</Button>
            {Object.entries(CATEGORY_LABELS).map(([key, label]) => (
              <Button key={key} variant={filterCat === key ? 'default' : 'outline'} size="sm" className="text-xs h-7" onClick={() => handleCatFilter(key)}>{label}</Button>
            ))}
          </div>

          {/* Main table */}
          <Card>
            <CardContent className="p-0">
              {/* Desktop table */}
              <div className="hidden sm:block">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead className="w-8"></TableHead>
                      <TableHead><SortableHeader label="Product" sortKey="product_name" currentSortKey={sortKey} sortDirection={sortDirection} onSort={handleSort} /></TableHead>
                      <TableHead><SortableHeader label="Category" sortKey="category" currentSortKey={sortKey} sortDirection={sortDirection} onSort={handleSort} /></TableHead>
                      <TableHead className="text-right"><SortableHeader label="On-Hand" sortKey="on_hand_kg" currentSortKey={sortKey} sortDirection={sortDirection} onSort={handleSort} className="justify-end" /></TableHead>
                      <TableHead className="text-right"><SortableHeader label="PO Pending" sortKey="po_pending_kg" currentSortKey={sortKey} sortDirection={sortDirection} onSort={handleSort} className="justify-end" /></TableHead>
                      <TableHead className="text-right"><SortableHeader label="SO Pending" sortKey="so_pending_kg" currentSortKey={sortKey} sortDirection={sortDirection} onSort={handleSort} className="justify-end" /></TableHead>
                      <TableHead className="text-right"><SortableHeader label="Available" sortKey="available_kg" currentSortKey={sortKey} sortDirection={sortDirection} onSort={handleSort} className="justify-end" /></TableHead>
                      <TableHead className="text-right"><SortableHeader label="Projected" sortKey="projected_kg" currentSortKey={sortKey} sortDirection={sortDirection} onSort={handleSort} className="justify-end" /></TableHead>
                      <TableHead className="w-10"></TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {sortedProducts.length === 0 ? (
                      <TableRow><TableCell colSpan={9} className="text-center py-8 text-muted-foreground">No products found</TableCell></TableRow>
                    ) : sortedProducts.map(p => (
                      <React.Fragment key={p.product_id}>
                        <TableRow
                          className={`cursor-pointer hover:bg-muted/50 ${expandedProduct === p.product_id ? 'bg-muted/30' : ''} ${p.is_low_stock ? 'bg-amber-50 dark:bg-amber-950/20' : ''}`}
                          onClick={() => toggleExpand(p.product_id)}
                          data-testid={`inv-row-${p.product_id}`}
                        >
                          <TableCell className="px-2">{expandedProduct === p.product_id ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}</TableCell>
                          <TableCell className="font-medium">
                            {p.product_name}
                            {p.is_low_stock && <AlertTriangle className="inline h-3.5 w-3.5 ml-1.5 text-amber-500" />}
                          </TableCell>
                          <TableCell><Badge variant="outline" className="text-xs">{CATEGORY_LABELS[p.category] || p.category}</Badge></TableCell>
                          <TableCell className="text-right font-mono font-semibold">{toTons(p.on_hand_kg)}</TableCell>
                          <TableCell className="text-right font-mono text-amber-600">{p.po_pending_kg > 0 ? toTons(p.po_pending_kg) : '-'}</TableCell>
                          <TableCell className="text-right font-mono text-orange-600">{p.so_pending_kg > 0 ? toTons(p.so_pending_kg) : '-'}</TableCell>
                          <TableCell className={`text-right font-mono font-semibold ${p.available_kg < 0 ? 'text-destructive' : 'text-green-600'}`}>{toTons(p.available_kg)}</TableCell>
                          <TableCell className="text-right font-mono text-violet-600">{toTons(p.projected_kg)}</TableCell>
                          <TableCell className="px-1" onClick={(e) => e.stopPropagation()}>
                            <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => openThresholdDialog(p)} title="Set low stock threshold" data-testid={`threshold-${p.product_id}`}><Settings2 className="h-3.5 w-3.5" /></Button>
                          </TableCell>
                        </TableRow>
                        {expandedProduct === p.product_id && (
                          <TableRow className="bg-muted/10">
                            <TableCell colSpan={9} className="p-0">
                              {!productBatches[p.product_id] ? <div className="p-4"><Skeleton className="h-16 w-full" /></div> : (
                                <div className="px-4 py-2">
                                  <div className="flex items-center justify-between mb-2">
                                    <h4 className="text-sm font-semibold">Batch Details — {p.product_name}</h4>
                                    <Button variant="outline" size="sm" className="h-7 text-xs" onClick={(e) => { e.stopPropagation(); openBatchDialog(p.product_id, p.product_name); }}>
                                      <Plus className="h-3 w-3 mr-1" />Add Batch
                                    </Button>
                                  </div>
                                  {p.low_stock_threshold > 0 && (
                                    <div className="text-xs text-muted-foreground mb-2">Low stock threshold: <span className="font-mono font-medium">{toTons(p.low_stock_threshold)} Tons</span></div>
                                  )}
                                  <Table>
                                    <TableHeader>
                                      <TableRow>
                                        <TableHead className="text-xs">Batch #</TableHead>
                                        <TableHead className="text-xs">Brand</TableHead>
                                        <TableHead className="text-xs">Date</TableHead>
                                        <TableHead className="text-xs text-right">KG</TableHead>
                                        <TableHead className="text-xs text-right">Bags</TableHead>
                                        <TableHead className="text-xs">Source</TableHead>
                                        <TableHead className="text-xs w-10"></TableHead>
                                      </TableRow>
                                    </TableHeader>
                                    <TableBody>
                                      {productBatches[p.product_id].batches.map(b => (
                                        <TableRow key={b.id}>
                                          <TableCell className="text-xs font-mono font-medium">{b.batch_number}</TableCell>
                                          <TableCell className="text-xs">{b.brand || '-'}</TableCell>
                                          <TableCell className="text-xs">{formatDate(b.date)}</TableCell>
                                          <TableCell className="text-xs text-right font-mono">{b.quantity_kg.toLocaleString()}</TableCell>
                                          <TableCell className="text-xs text-right font-mono">{b.quantity_bags?.toLocaleString() || '-'}</TableCell>
                                          <TableCell className="text-xs"><Badge variant="outline" className="text-[10px]">{b.source_type === 'purchase_entry' ? 'Purchase' : 'Manual'}</Badge></TableCell>
                                          <TableCell>
                                            {b.source_type === 'manual' && (
                                              <Button variant="ghost" size="icon" className="h-6 w-6 text-destructive hover:text-destructive" onClick={(e) => { e.stopPropagation(); handleDeleteBatch(b); }}>
                                                <Trash2 className="h-3 w-3" />
                                              </Button>
                                            )}
                                          </TableCell>
                                        </TableRow>
                                      ))}
                                    </TableBody>
                                  </Table>
                                </div>
                              )}
                            </TableCell>
                          </TableRow>
                        )}
                      </React.Fragment>
                    ))}
                  </TableBody>
                </Table>
              </div>

              {/* Mobile card view */}
              <div className="sm:hidden divide-y">
                {sortedProducts.length === 0 ? (
                  <p className="text-center py-8 text-muted-foreground text-sm">No products found</p>
                ) : sortedProducts.map(p => (
                  <div key={p.product_id} data-testid={`inv-m-${p.product_id}`}>
                    <div
                      className={`px-3 py-2.5 cursor-pointer active:bg-muted/50 ${expandedProduct === p.product_id ? 'bg-muted/30' : ''} ${p.is_low_stock ? 'bg-amber-50 dark:bg-amber-950/20' : ''}`}
                      onClick={() => toggleExpand(p.product_id)}
                    >
                      <div className="flex items-center justify-between mb-1">
                        <span className="font-medium text-sm">{p.product_name} {p.is_low_stock && <AlertTriangle className="inline h-3 w-3 text-amber-500" />}</span>
                        <Badge variant="outline" className="text-[10px]">{CATEGORY_LABELS[p.category] || p.category}</Badge>
                      </div>
                      <div className="grid grid-cols-5 gap-1 text-[10px] text-muted-foreground">
                        <span className="text-center">On-Hand</span>
                        <span className="text-center">PO Pend</span>
                        <span className="text-center">SO Pend</span>
                        <span className="text-center">Avail</span>
                        <span className="text-center">Projected</span>
                      </div>
                      <div className="grid grid-cols-5 gap-1 text-xs font-mono text-center">
                        <span className="font-semibold">{toTons(p.on_hand_kg)}</span>
                        <span className="text-amber-600">{p.po_pending_kg > 0 ? toTons(p.po_pending_kg) : '-'}</span>
                        <span className="text-orange-600">{p.so_pending_kg > 0 ? toTons(p.so_pending_kg) : '-'}</span>
                        <span className={`font-semibold ${p.available_kg < 0 ? 'text-destructive' : 'text-green-600'}`}>{toTons(p.available_kg)}</span>
                        <span className="text-violet-600">{toTons(p.projected_kg)}</span>
                      </div>
                    </div>
                    {expandedProduct === p.product_id && (
                      <div className="bg-muted/10 px-3 py-2 border-t">
                        {!productBatches[p.product_id] ? <Skeleton className="h-12 w-full" /> : (
                          <div className="space-y-2">
                            <div className="flex items-center justify-between">
                              <span className="text-xs font-semibold">Batches</span>
                              <div className="flex gap-2">
                                <Button variant="ghost" size="sm" className="h-6 text-[10px]" onClick={(e) => { e.stopPropagation(); openThresholdDialog(p); }}><Settings2 className="h-3 w-3 mr-1" />Threshold</Button>
                                <Button variant="outline" size="sm" className="h-6 text-[10px]" onClick={(e) => { e.stopPropagation(); openBatchDialog(p.product_id, p.product_name); }}><Plus className="h-3 w-3 mr-1" />Batch</Button>
                              </div>
                            </div>
                            {productBatches[p.product_id].batches.map(b => (
                              <div key={b.id} className="bg-background border rounded px-2 py-1.5 text-xs flex items-center justify-between">
                                <div>
                                  <span className="font-mono font-medium">{b.batch_number}</span>
                                  {b.brand && <span className="ml-1.5 text-muted-foreground">({b.brand})</span>}
                                  <span className="ml-1.5 text-muted-foreground">{formatDate(b.date)}</span>
                                </div>
                                <div className="flex items-center gap-2">
                                  <span className="font-mono">{b.quantity_kg.toLocaleString()} KG</span>
                                  {b.source_type === 'manual' && (
                                    <Button variant="ghost" size="icon" className="h-5 w-5 text-destructive" onClick={(e) => { e.stopPropagation(); handleDeleteBatch(b); }}><Trash2 className="h-3 w-3" /></Button>
                                  )}
                                </div>
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        </>
      )}

      {/* Batch Dialog */}
      <Dialog open={batchDialogOpen} onOpenChange={setBatchDialogOpen}>
        <DialogContent>
          <DialogHeader><DialogTitle>Add Batch {batchProductName ? `— ${batchProductName}` : ''}</DialogTitle></DialogHeader>
          <EnterForm onSubmit={handleCreateBatch} className="space-y-4">
            {batchError && <Alert variant="destructive"><AlertCircle className="h-4 w-4" /><AlertDescription>{batchError}</AlertDescription></Alert>}
            {!batchForm.product_id && (
              <div className="space-y-2"><Label>Product</Label>
                <SearchableSelect options={productOptions} value={batchForm.product_id} onChange={handleBatchProductChange} placeholder="Select product" data-testid="batch-product-select" />
              </div>
            )}
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2"><Label>Batch Number</Label><Input value={batchForm.batch_number} onChange={(e) => setBatchForm(p => ({...p, batch_number: e.target.value}))} placeholder="Auto-generated or custom" /></div>
              <div className="space-y-2"><Label>Date</Label><DateInput value={batchForm.date} onChange={(e) => setBatchForm(p => ({...p, date: e.target.value}))} required /></div>
            </div>
            <div className="space-y-2"><Label>Brand</Label><Input value={batchForm.brand} onChange={(e) => setBatchForm(p => ({...p, brand: e.target.value}))} placeholder="Optional" /></div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2"><Label>Qty (KG)</Label><Input type="number" step="0.01" min="0" value={batchForm.quantity_kg} onChange={(e) => handleBatchKgChange(e.target.value)} required /></div>
              <div className="space-y-2"><Label>Bags</Label><Input type="number" step="0.1" value={batchForm.quantity_bags} readOnly tabIndex={-1} className="bg-muted/50 cursor-default" /></div>
            </div>
            <div className="space-y-2"><Label>Remarks</Label><Input value={batchForm.remarks} onChange={(e) => setBatchForm(p => ({...p, remarks: e.target.value}))} placeholder="Optional" /></div>
            <DialogFooter>
              <FormShortcutHints />
              <Button type="button" variant="outline" onClick={() => setBatchDialogOpen(false)}>Cancel</Button>
              <Button type="submit" disabled={batchSubmitting || !batchForm.product_id || !batchForm.quantity_kg}>{batchSubmitting ? 'Saving...' : 'Create Batch'}</Button>
            </DialogFooter>
          </EnterForm>
        </DialogContent>
      </Dialog>

      {/* Threshold Dialog */}
      <Dialog open={thresholdDialog} onOpenChange={setThresholdDialog}>
        <DialogContent className="max-w-sm">
          <DialogHeader><DialogTitle>Low Stock Threshold</DialogTitle></DialogHeader>
          {thresholdProduct && (
            <div className="space-y-4">
              <p className="text-sm text-muted-foreground">Set minimum available stock for <span className="font-medium text-foreground">{thresholdProduct.product_name}</span>. Alert will show when Available drops below this.</p>
              <div className="space-y-2">
                <Label>Threshold (KG)</Label>
                <Input type="number" step="1" min="0" value={thresholdValue} onChange={(e) => setThresholdValue(e.target.value)} placeholder="e.g. 5000" autoFocus data-testid="threshold-input" />
                {thresholdValue && <p className="text-xs text-muted-foreground">{toTons(parseFloat(thresholdValue) || 0)} Tons</p>}
              </div>
              <DialogFooter>
                <Button variant="outline" onClick={() => setThresholdDialog(false)}>Cancel</Button>
                <Button onClick={handleSaveThreshold} disabled={thresholdSubmitting} data-testid="threshold-save">{thresholdSubmitting ? 'Saving...' : 'Save'}</Button>
              </DialogFooter>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Inventory;
